import React, { useState, useCallback } from 'react';
import { formatDate, formatWeight } from '../../utils/formatHelpers';

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const ShipmentsChart = ({ data, loading, drillData, drillLoading, onBarClick, onDrillClose }) => {
  const [hoveredBar, setHoveredBar] = useState(null);

  const handleBarClick = useCallback((month) => {
    if (onBarClick) onBarClick(month);
  }, [onBarClick]);

  if (loading) return <div className="chart-loading">Loading shipment data...</div>;
  if (!data || data.length === 0) {
    return <div className="chart-empty">No shipment data available for this period.</div>;
  }

  const currentYear = data.filter(d => d.year === 'current');
  const prevYear    = data.filter(d => d.year === 'prev');
  const maxVal = Math.max(...data.map(d => d.count || 0), 1);

  return (
    <div className="chart-section">
      <div className="bar-chart" role="img" aria-label="Monthly shipments bar chart">
        <div className="bar-chart__bars">
          {MONTH_LABELS.map((label, idx) => {
            const cur  = currentYear.find(d => d.month === idx + 1) || { count: 0 };
            const prev = prevYear.find(d => d.month === idx + 1)    || { count: 0 };
            const curH  = Math.round((cur.count  / maxVal) * 180);
            const prevH = Math.round((prev.count / maxVal) * 180);
            const isActive = drillData?.month === idx + 1;

            return (
              <div
                key={idx}
                className={`bar-col ${isActive ? 'bar-col--active' : ''}`}
                onClick={() => handleBarClick({ month: idx + 1, label })}
                onMouseEnter={() => setHoveredBar(idx)}
                onMouseLeave={() => setHoveredBar(null)}
                role="button"
                tabIndex={0}
                aria-label={`${label}: ${cur.count} shipments. Click for details.`}
                onKeyDown={e => e.key === 'Enter' && handleBarClick({ month: idx + 1, label })}
              >
                <div className="bar-pair">
                  {prevH > 0 && (
                    <div
                      className="bar bar--prev"
                      style={{ height: prevH + 'px' }}
                      title={`${label} (prev year): ${prev.count}`}
                    />
                  )}
                  <div
                    className="bar bar--cur"
                    style={{ height: Math.max(curH, cur.count > 0 ? 4 : 0) + 'px' }}
                    title={`${label}: ${cur.count}`}
                  />
                </div>
                {hoveredBar === idx && cur.count > 0 && (
                  <div className="bar-tooltip">{cur.count}</div>
                )}
                <span className="bar-label">{label}</span>
              </div>
            );
          })}
        </div>
        <div className="chart-legend">
          <span className="legend-item legend-item--cur">Current Year</span>
          <span className="legend-item legend-item--prev">Previous Year</span>
        </div>
      </div>

      {drillData && (
        <div className="drill-down">
          <div className="drill-down__header">
            <h3 className="drill-down__title">
              Shipments — {MONTH_LABELS[(drillData.month || 1) - 1]}
            </h3>
            <button className="drill-down__close" onClick={onDrillClose} aria-label="Close drill-down">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              Close
            </button>
          </div>

          {drillLoading ? (
            <div className="drill-down__loading">Loading...</div>
          ) : !drillData.rows || drillData.rows.length === 0 ? (
            <div className="drill-down__empty">No shipments found for this month.</div>
          ) : (
            <div className="results-table-container">
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Shipment #</th>
                    <th>Customer</th>
                    <th>Pallets</th>
                    <th>Weight</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {drillData.rows.map(s => (
                    <tr key={s.id}>
                      <td className="shipment-number">{s.shipment_number}</td>
                      <td>{s.customer_name}</td>
                      <td>{s.pallet_count || 0}</td>
                      <td>{formatWeight(s.total_weight)}</td>
                      <td><span className={`status-badge ${s.status}`}>{s.status}</span></td>
                      <td>{formatDate(s.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ShipmentsChart;
